/**
 * M6 — Calendario Time-Slot por Técnico
 * ═══════════════════════════════════════════════════════════════════
 * Vista día:
 *   • Eje Y  = franjas horarias (07:00 → 19:00, cada 30 min configurable)
 *   • Eje X  = columnas por técnico
 *   • Cada OT se dibuja como bloque color-coded por estado
 *   • Drag & drop: mover un bloque a otra hora/técnico ejecuta
 *     PATCH /v1/work-orders/{id} { scheduled_at, technician_id }
 *   • Zona lateral "Sin agendar" acepta drop para desasignar hora
 *   • Tooltip con detalles al hover; click abre detalle de OT
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import FloatingPanel from "../components/ui/FloatingPanel";   // v101.98.0
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useGlobalFilters } from "../lib/globalFilters";
import {
  Calendar as CalIcon,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock,
  Users,
  Filter,
  RefreshCcw,
  Inbox,
  MapPin,
} from "lucide-react";

const STATUS_COLOR = {
  pendiente_agendamiento: { bg: "#F1F5F9", border: "#94A3B8", text: "#334155", label: "PTE" },
  asignada: { bg: "#DBEAFE", border: "#2563EB", text: "#1E3A8A", label: "ASG" },
  en_ruta: { bg: "#CFFAFE", border: "#0891B2", text: "#164E63", label: "RUT" },
  en_ejecucion: { bg: "#E0F2FE", border: "#0EA5E9", text: "#075985", label: "EJE" },
  pendiente_fiscalizacion: { bg: "#FEF3C7", border: "#D97706", text: "#78350F", label: "FIS" },
  validada: { bg: "#DCFCE7", border: "#16A34A", text: "#14532D", label: "VAL" },
  con_observaciones: { bg: "#FEE2E2", border: "#DC2626", text: "#7F1D1D", label: "OBS" },
  rechazada: { bg: "#FEE2E2", border: "#B91C1C", text: "#7F1D1D", label: "RCH" },
  prefacturada: { bg: "#D1FAE5", border: "#059669", text: "#065F46", label: "PRE" },
  cerrada: { bg: "#E2E8F0", border: "#0A3747", text: "#0A3747", label: "CER" },
  cancelada: { bg: "#F3F4F6", border: "#6B7280", text: "#374151", label: "CAN" },
  // v2.8.7.3 · Propuesta A2 (dry-run, no aplicada aún) — estilo distintivo gris punteado
  propuesta_a2: { bg: "#F3F4F6", border: "#9CA3AF", text: "#4B5563", label: "A2·PRO" },
};

const START_HOUR = 7;
const END_HOUR = 19;
const SLOT_MINUTES = 30;
const SLOT_HEIGHT = 36; // px por slot de 30min → 72px por hora

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const totalSlots = ((END_HOUR - START_HOUR) * 60) / SLOT_MINUTES;

// Convierte una hora "hh:mm" o un ISO string a índice de slot (0..totalSlots-1)
const slotIndexForDate = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const minutes = dt.getHours() * 60 + dt.getMinutes();
  const base = START_HOUR * 60;
  const idx = Math.round((minutes - base) / SLOT_MINUTES);
  return Math.max(0, Math.min(totalSlots - 1, idx));
};

// Convierte índice de slot a Date (en la fecha `dayDate`)
const dateForSlot = (dayDate, slotIdx) => {
  const dt = new Date(dayDate);
  const minutes = START_HOUR * 60 + slotIdx * SLOT_MINUTES;
  dt.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return dt;
};

const formatSlotLabel = (idx) => {
  const minutes = START_HOUR * 60 + idx * SLOT_MINUTES;
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
};

// v3.1.2 · Filtro multi-zona con dropdown de checkboxes (P1 UX #17)
function MultiZoneFilter({ zones, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const zoneBtnRef = useRef(null);   // v101.98.0
  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    onChange([...s]);
  };
  const clear = () => onChange([]);
  const summary = selected.length === 0
    ? "Todas las zonas"
    : selected.length === 1
      ? (zones.find((z) => z.id === selected[0])?.name || "1 zona")
      : `${selected.length} zonas`;
  return (
    <div className="relative">
      <button
        ref={zoneBtnRef}
        onClick={() => setOpen((v) => !v)}
        className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white flex items-center gap-1 hover:bg-gray-50"
        data-testid="cal-zone-multifilter-toggle"
      >
        📍 {summary}
        <span className="text-gray-400 ml-1">▾</span>
      </button>
      {open && (
        <FloatingPanel anchorRef={zoneBtnRef} maxHeight={288} className="bg-white border border-gray-200 rounded shadow-lg w-64" data-testid="cal-zone-multifilter-panel">
          <div className="p-2 border-b flex items-center justify-between bg-slate-50 sticky top-0">
            <button onClick={clear} className="text-[10px] text-blue-600 hover:underline" data-testid="cal-zone-clear">Limpiar</button>
            <span className="text-[10px] text-gray-500">{selected.length} seleccionadas</span>
            <button onClick={() => setOpen(false)} className="text-[10px] text-gray-500 hover:text-gray-700">✕</button>
          </div>
          {zones.map((z) => (
            <label key={z.id} className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0">
              <input
                type="checkbox"
                checked={selected.includes(z.id)}
                onChange={() => toggle(z.id)}
                data-testid={`cal-zone-chk-${z.id}`}
              />
              <span className="flex-1 truncate">{z.name}</span>
              {z.code && <span className="text-[9px] text-gray-400 font-mono">{z.code}</span>}
            </label>
          ))}
        </FloatingPanel>
      )}
    </div>
  );
}

export default function CalendarioTecnicos() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("M1.edit") || hasPerm("M1.assign");

  // v9.0 · Conectar filtros globales
  const { filters, setFilter } = useGlobalFilters();
  const globalBranch = filters.branch_office_id || "";
  const globalStatus = filters.internal_status || "";
  const globalTechnician = filters.technician_id || "";

  const [anchor, setAnchor] = useState(new Date());
  const [viewMode, setViewMode] = useState("day"); // day | week | month
  const [orders, setOrders] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [filterZone, setFilterZone] = useState("");
  const [zones, setZones] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all"); // all | active
  const [dragging, setDragging] = useState(null); // {order}

  const iso = toISODate(anchor);

  // v1.9.11 · Rango según vista (día/semana/mes)
  const rangeDates = useMemo(() => {
    if (viewMode === "day") return { from: iso, to: iso, days: 1 };
    if (viewMode === "week") {
      const start = new Date(anchor);
      start.setDate(start.getDate() - start.getDay()); // domingo
      const end = new Date(start); end.setDate(end.getDate() + 6);
      return { from: toISODate(start), to: toISODate(end), days: 7, startDate: start };
    }
    // month: rango del calendario mensual (con overflow de días previos/siguientes)
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const gridStart = new Date(first); gridStart.setDate(gridStart.getDate() - first.getDay());
    const gridEnd = new Date(last); gridEnd.setDate(gridEnd.getDate() + (6 - last.getDay()));
    const days = Math.round((gridEnd - gridStart) / 86400000) + 1;
    return { from: toISODate(gridStart), to: toISODate(gridEnd), days, startDate: gridStart };
  }, [viewMode, anchor, iso]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordRes, techRes, zoneRes] = await Promise.all([
        api.get("/v1/work-orders", {
          params: { from_date: rangeDates.from, to_date: rangeDates.to, page: 1, page_size: 500 },
        }),
        api.get("/v1/users/technicians"),
        api.get("/v1/config/zones").catch(() => ({ data: { items: [] } })),
      ]);
      const pendingRes = await api.get("/v1/work-orders", {
        params: { status: "pendiente_agendamiento", page: 1, page_size: 200 },
      }).catch(() => ({ data: { items: [] } }));

      // v9.0 · Fetch OTs from master_ot via dedicated calendar endpoint
      const smartRes = await api.get("/v1/ot_smart/calendar-data", {
        params: {
          from_date: rangeDates.from,
          to_date: rangeDates.to,
          ...(globalBranch ? { branch_office_id: globalBranch } : {}),
          ...(globalTechnician ? { technician_id: globalTechnician } : {}),
          ...(globalStatus ? { internal_status: globalStatus } : {}),
        },
      }).catch(() => ({ data: { items: [] } }));

      // v2.8.7.3 · Traer propuestas A2 no aplicadas para superponer en el calendario
      const proposalsRes = await api.get("/a2/proposals-active").catch(() => ({ data: { items: [] } }));

      const merged = [...(ordRes.data.items || [])];
      const seen = new Set(merged.map((o) => o.id));
      for (const p of pendingRes.data.items || []) {
        if (!seen.has(p.id)) { merged.push(p); seen.add(p.id); }
      }

      // v9.0 · Adapt master_ot items to calendar shape
      const smartItems = smartRes.data?.items || [];
      for (const ot of smartItems) {
        const calId = `smart-${ot.odoo_task_id}`;
        if (!seen.has(calId)) {
          merged.push({
            id: calId,
            odoo_task_id: ot.odoo_task_id,
            code: ot.afc_code || ot.task_number || `OT-${ot.odoo_task_id}`,
            client_name: ot.client_name || "",
            activity_type: ot.activity_type_code || ot.solution_type_name || ot.plan_name || "",
            technician_id: ot.technician_id,
            technician_name: ot.technician_name || "",
            assigned_user_id: ot.technician_id,
            scheduled_at: ot.scheduled_at || ot.created_at,
            scheduled_end_at: null,
            status: mapSmartToCalStatus(ot.internal_status),
            internal_status: ot.internal_status,
            sla_due_at: ot.sla_due_at,
            _from_master_ot: true,
          });
          seen.add(calId);
        }
      }

      // Adaptar propuestas al shape de work-order para renderizarse en el calendario
      const proposalItems = (proposalsRes.data?.items || []).map((p) => ({
        id: `a2-proposal-${p.odoo_task_id}`,
        code: p.code,
        client_name: p.client,
        activity_type: p.activity_type,
        technician_id: p.tech_id,
        technician_name: p.tech_name,
        assigned_user_id: p.tech_id,
        // Bloque completo (traslado+ejec+regreso) al calendario, pero exec al preview
        scheduled_at: p.exec_start || p.slot_start,
        scheduled_end_at: p.exec_end || p.slot_end,
        status: "propuesta_a2",  // key único para color-code
        _proposal: p,
      }));
      setOrders([...merged, ...proposalItems]);
      setTechs(techRes.data.items || techRes.data || []);
      const zList = zoneRes.data?.items || zoneRes.data || [];
      setZones(Array.isArray(zList) ? zList : []);
    } catch (e) {
      toast.error("Error al cargar calendario");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // v9.0 · Helper para mapear status master_ot → calendar status
  function mapSmartToCalStatus(s) {
    const m = {
      pending: "pendiente_agendamiento",
      assigned: "asignada",
      in_progress: "en_ejecucion",
      on_site: "en_ejecucion",
      completed: "cerrada",
      cancelled: "cancelada",
    };
    return m[(s || "").toLowerCase()] || "asignada";
  }

  useEffect(() => {
    fetchData();
  }, [rangeDates.from, rangeDates.to]);
  const scheduledOrders = useMemo(
    () => orders.filter((o) => (o.scheduled_at || "").slice(0, 10) === iso),
    [orders, iso]
  );
  // v2.8.7.3 · Todas las OTs con scheduled_at (para vistas Semana/Mes)
  const allScheduledOrders = useMemo(
    () => orders.filter((o) => !!o.scheduled_at),
    [orders]
  );

  // Sin agendar: sin scheduled_at o pendiente_agendamiento sin fecha
  const unscheduledOrders = useMemo(
    () =>
      orders.filter(
        (o) =>
          !o.scheduled_at ||
          (o.status === "pendiente_agendamiento" && !o.scheduled_at)
      ),
    [orders]
  );

  // v3.1.2 · Filtro multi-zona (P1 UX #17): filterZone puede ser array
  // v9.0 · Integrar filtros globales (branch, status, technician)
  const applyFilters = (list) => {
    let out = list;
    if (Array.isArray(filterZone) && filterZone.length > 0) {
      const set = new Set(filterZone);
      out = out.filter((o) => set.has(o.zone_id));
    } else if (typeof filterZone === "string" && filterZone) {
      out = out.filter((o) => o.zone_id === filterZone);
    }
    if (statusFilter === "active")
      out = out.filter(
        (o) => !["cerrada", "cancelada", "validada"].includes(o.status)
      );
    // v9.0 · Global filters
    if (globalStatus) {
      out = out.filter((o) => (o.status || "").includes(globalStatus));
    }
    if (globalTechnician) {
      out = out.filter((o) => String(o.technician_id || o.assigned_user_id) === String(globalTechnician));
    }
    return out;
  };

  const scheduledFiltered = applyFilters(scheduledOrders);
  const scheduledAllFiltered = applyFilters(allScheduledOrders);
  const unscheduledFiltered = applyFilters(unscheduledOrders);

  // Técnicos: solo mostrar los activos + agregar "Sin asignar" al final
  const techColumns = useMemo(() => {
    const cols = techs
      .filter((t) => t.active !== false)
      .map((t) => ({ id: t.id, name: t.name, role: t.role }));
    // añadir columna virtual "sin asignar" al inicio si hay OTs sin técnico ese día
    return [{ id: "__unassigned__", name: "Sin asignar", virtual: true }, ...cols];
  }, [techs]);

  // Agrupar OTs por técnico
  const ordersByTech = useMemo(() => {
    const map = {};
    for (const c of techColumns) map[c.id] = [];
    for (const o of scheduledFiltered) {
      const tid = o.technician_id || o.assigned_user_id || "__unassigned__";
      if (!map[tid]) map[tid] = [];
      map[tid].push(o);
    }
    return map;
  }, [scheduledFiltered, techColumns]);

  const handleDropOnSlot = async (techId, slotIdx) => {
    if (!dragging || !canEdit) return;
    const order = dragging;
    setDragging(null);
    const targetDate = dateForSlot(anchor, slotIdx);
    const newScheduled = targetDate.toISOString();
    const newTech = techId === "__unassigned__" ? null : techId;

    // Optimista: actualizar UI inmediatamente
    setOrders((prev) =>
      prev.map((o) =>
        o.id === order.id
          ? { ...o, scheduled_at: newScheduled, technician_id: newTech }
          : o
      )
    );
    setSavingId(order.id);
    try {
      const payload = { scheduled_at: newScheduled };
      // Solo enviar technician_id si cambia efectivamente y no es null->null
      const currentTech = order.technician_id || order.assigned_user_id || null;
      if (newTech !== currentTech) {
        if (newTech) payload.technician_id = newTech;
      }
      await api.patch(`/v1/work-orders/${order.id}`, payload);
      toast.success(`OT ${order.code || order.id.slice(0, 8)} reprogramada`);
      // refetch limitado para hidratar technician_name
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al reprogramar";
      toast.error(msg);
      // revertir
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? { ...o, scheduled_at: order.scheduled_at, technician_id: order.technician_id }
            : o
        )
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleDropOnUnscheduled = async () => {
    if (!dragging || !canEdit) return;
    const order = dragging;
    setDragging(null);
    if (!order.scheduled_at) return; // ya está sin agendar
    setOrders((prev) =>
      prev.map((o) => (o.id === order.id ? { ...o, scheduled_at: null } : o))
    );
    setSavingId(order.id);
    try {
      // Enviar null explicito: el backend acepta scheduled_at nullable en el modelo
      await api.patch(`/v1/work-orders/${order.id}`, { scheduled_at: null });
      toast.success(`OT ${order.code || order.id.slice(0, 8)} desagendada`);
      fetchData();
    } catch (e) {
      // El PATCH descarta None por sparse update. Fallback: usar un endpoint alterno o revertir.
      toast.warning("El backend no permite desagendar por PATCH; recargando...");
      fetchData();
    } finally {
      setSavingId(null);
    }
  };

  const navStep = viewMode === "day" ? 1 : (viewMode === "week" ? 7 : 30);
  const rangeLabel = viewMode === "day"
    ? anchor.toLocaleDateString("es-EC", {timeZone: "America/Guayaquil",  timeZone: "America/Guayaquil", weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : viewMode === "week" && rangeDates.startDate
      ? `Semana del ${rangeDates.startDate.toLocaleDateString("es-EC", {timeZone: "America/Guayaquil",  timeZone: "America/Guayaquil", day: "numeric", month: "short" })} al ${new Date(rangeDates.startDate.getTime() + 6*86400000).toLocaleDateString("es-EC", {timeZone: "America/Guayaquil",  timeZone: "America/Guayaquil", day: "numeric", month: "short", year: "numeric" })}`
      : anchor.toLocaleDateString("es-EC", {timeZone: "America/Guayaquil",  timeZone: "America/Guayaquil", month: "long", year: "numeric" });

  return (
    <Layout
      title="Calendario de Técnicos"
      subtitle={`Vista ${viewMode === "day" ? "día · drag & drop" : viewMode === "week" ? "semana · estilo Google Calendar" : "mes · estilo Google Calendar"}`}
      wide
      actions={
        <div className="flex items-center gap-2">
          {/* v1.9.11 · Selector Día/Semana/Mes estilo Google Calendar */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden bg-white">
            {["day", "week", "month"].map((v) => (
              <button
                key={v}
                onClick={() => setViewMode(v)}
                className={`px-3 py-1.5 text-xs font-bold ${viewMode === v ? "bg-[#0A3747] text-[#E0E621]" : "text-gray-600 hover:bg-gray-50"}`}
                data-testid={`cal-view-${v}`}
              >
                {v === "day" ? "Día" : v === "week" ? "Semana" : "Mes"}
              </button>
            ))}
          </div>
          <button
            onClick={fetchData}
            className="p-2 border border-gray-300 bg-white rounded hover:bg-gray-50"
            title="Refrescar"
            data-testid="cal-refresh"
          >
            <RefreshCcw size={14} />
          </button>
        </div>
      }
    >
      {/* Toolbar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAnchor(addDays(anchor, -navStep))}
            className="p-2 border border-gray-300 bg-white rounded hover:bg-gray-50"
            data-testid="cal-prev-day"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => setAnchor(new Date())}
            className="px-3 py-1.5 text-xs font-bold rounded bg-[#0A3747] text-[#E0E621]"
            data-testid="cal-today"
          >
            HOY
          </button>
          <button
            onClick={() => setAnchor(addDays(anchor, navStep))}
            className="p-2 border border-gray-300 bg-white rounded hover:bg-gray-50"
            data-testid="cal-next-day"
          >
            <ChevronRight size={14} />
          </button>
          <div className="ml-3 text-sm font-bold text-[#0A3747] uppercase tracking-wider">
            <CalIcon size={14} className="inline mr-2" />
            {rangeLabel}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* v3.1.2 · Multi-zona con dropdown de checkboxes */}
          <MultiZoneFilter
            zones={zones}
            selected={Array.isArray(filterZone) ? filterZone : (filterZone ? [filterZone] : [])}
            onChange={(arr) => setFilterZone(arr)}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
            data-testid="cal-status-filter"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Solo activas</option>
          </select>
          <div className="text-[11px] text-gray-500 flex items-center gap-1 border-l border-gray-200 pl-3 ml-1">
            <Users size={12} /> {techColumns.length - 1} técnicos ·{" "}
            <Clock size={12} /> {scheduledFiltered.length} OTs hoy
          </div>
        </div>
      </div>

      {/* Grid principal · Día = time-slot con técnicos · Semana/Mes = estilo Google Calendar */}
      <div className="flex gap-3">
        <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center">
              <Loader2 className="animate-spin text-gray-400" />
            </div>
          ) : viewMode === "day" ? (
            <TimeSlotGrid
              anchor={anchor}
              techColumns={techColumns}
              ordersByTech={ordersByTech}
              onDragStart={(o) => setDragging(o)}
              onDragEnd={() => setDragging(null)}
              onDropOnSlot={handleDropOnSlot}
              onOpenOrder={(id) => {
                // v2.8.7.3 · Propuestas A2 no navegan al detalle (ID sintético)
                if (typeof id === "string" && id.startsWith("a2-proposal-")) {
                  toast.info("Propuesta A2 · Aplícala desde el Simulador A2 para agendarla");
                  navigate("/admin/a2-dryrun");
                  return;
                }
                navigate(`/ordenes/${id}`);
              }}
              savingId={savingId}
              canEdit={canEdit}
            />
          ) : viewMode === "week" ? (
            <WeekView
              startDate={rangeDates.startDate}
              orders={scheduledAllFiltered}
              onOpenOrder={(id) => {
                if (typeof id === "string" && id.startsWith("a2-proposal-")) {
                  toast.info("Propuesta A2 · Aplícala desde el Simulador A2");
                  navigate("/admin/a2-dryrun");
                  return;
                }
                navigate(`/ordenes/${id}`);
              }}
            />
          ) : (
            <MonthView
              startDate={rangeDates.startDate}
              anchor={anchor}
              orders={scheduledAllFiltered}
              onOpenOrder={(id) => {
                if (typeof id === "string" && id.startsWith("a2-proposal-")) {
                  toast.info("Propuesta A2 · Aplícala desde el Simulador A2");
                  navigate("/admin/a2-dryrun");
                  return;
                }
                navigate(`/ordenes/${id}`);
              }}
              onPickDay={(d) => { setAnchor(d); setViewMode("day"); }}
            />
          )}
        </div>

        {/* Sidebar: Sin agendar · sólo en vista Día */}
        {viewMode === "day" && (
          <UnscheduledSidebar
            orders={unscheduledFiltered}
            onDragStart={(o) => setDragging(o)}
            onDragEnd={() => setDragging(null)}
            onDrop={handleDropOnUnscheduled}
            onOpenOrder={(id) => navigate(`/ordenes/${id}`)}
            savingId={savingId}
          />
        )}
      </div>

      {/* Leyenda */}
      <div className="mt-3 bg-white border border-gray-200 rounded-lg px-3 py-2 flex flex-wrap gap-2">
        <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mr-2">
          Leyenda
        </div>
        {Object.entries(STATUS_COLOR).map(([k, c]) => (
          <div
            key={k}
            className="flex items-center gap-1.5 text-[10px] font-mono"
            title={k === "propuesta_a2" ? "Propuesta A2 · No aplicada aún (dry-run)" : k}
          >
            <span
              className="w-3 h-3 rounded border"
              style={{
                backgroundColor: c.bg,
                borderColor: c.border,
                ...(k === "propuesta_a2" ? { borderStyle: "dashed", borderWidth: "1.5px" } : {}),
              }}
            />
            <span className="text-gray-700">{c.label}</span>
          </div>
        ))}
      </div>
    </Layout>
  );
}

/* ─── Grilla principal ─────────────────────────────────── */
function TimeSlotGrid({
  anchor,
  techColumns,
  ordersByTech,
  onDragStart,
  onDragEnd,
  onDropOnSlot,
  onOpenOrder,
  savingId,
  canEdit,
}) {
  const hours = [];
  for (let i = 0; i < totalSlots; i++) hours.push(i);

  return (
    <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 260px)" }}>
      <div
        className="grid"
        style={{
          gridTemplateColumns: `70px repeat(${techColumns.length}, minmax(180px, 1fr))`,
        }}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-20 bg-[#0A3747] text-[#E0E621] px-2 py-2 text-[10px] font-mono uppercase tracking-wider">
          Hora
        </div>
        {techColumns.map((c) => (
          <div
            key={c.id}
            className={`sticky top-0 z-20 px-2 py-2 text-[11px] font-bold border-l border-white/10 truncate ${
              c.virtual
                ? "bg-amber-500 text-white"
                : "bg-[#0A3747] text-white"
            }`}
            title={c.name}
            data-testid={`tech-col-${c.id}`}
          >
            {c.name}
          </div>
        ))}

        {/* Filas de slots */}
        {hours.map((slotIdx) => {
          const label = formatSlotLabel(slotIdx);
          const isFullHour = slotIdx % 2 === 0;
          return (
            <div key={slotIdx} className="contents">
              <div
                className={`px-2 flex items-start justify-end text-[10px] font-mono border-t ${
                  isFullHour
                    ? "bg-gray-50 text-gray-700 border-gray-200 font-bold"
                    : "bg-white text-gray-300 border-gray-100"
                }`}
                style={{ height: SLOT_HEIGHT }}
              >
                {isFullHour ? label : ""}
              </div>
              {techColumns.map((c) => (
                <SlotCell
                  key={`${c.id}-${slotIdx}`}
                  techId={c.id}
                  slotIdx={slotIdx}
                  anchor={anchor}
                  orders={ordersByTech[c.id] || []}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDrop={() => onDropOnSlot(c.id, slotIdx)}
                  onOpenOrder={onOpenOrder}
                  savingId={savingId}
                  isFullHour={isFullHour}
                  canEdit={canEdit}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Celda individual (drop target) ──────────────────────── */
function SlotCell({
  techId,
  slotIdx,
  anchor,
  orders,
  onDragStart,
  onDragEnd,
  onDrop,
  onOpenOrder,
  savingId,
  isFullHour,
  canEdit,
}) {
  const [hover, setHover] = useState(false);
  const cellDate = dateForSlot(anchor, slotIdx);

  // OTs cuyo slot inicial es este slot
  const inThisSlot = orders.filter(
    (o) => o.scheduled_at && slotIndexForDate(o.scheduled_at) === slotIdx
  );

  const handleDragOver = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    setHover(true);
  };
  const handleDragLeave = () => setHover(false);
  const handleDropEvt = (e) => {
    e.preventDefault();
    setHover(false);
    onDrop();
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDropEvt}
      className={`border-t border-l relative ${
        isFullHour ? "border-gray-200" : "border-gray-100"
      } ${hover ? "bg-lime-50 ring-2 ring-inset ring-[#E0E621]" : "bg-white"}`}
      style={{ height: SLOT_HEIGHT }}
      data-testid={`slot-${techId}-${slotIdx}`}
    >
      {inThisSlot.map((o) => (
        <OrderBlock
          key={o.id}
          order={o}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onOpen={() => onOpenOrder(o.id)}
          saving={savingId === o.id}
          canDrag={canEdit}
        />
      ))}
    </div>
  );
}

/* ─── Bloque de OT (draggable) ────────────────────────────── */
function OrderBlock({ order, onDragStart, onDragEnd, onOpen, saving, canDrag }) {
  const status = order.status || "pendiente_agendamiento";
  const c = STATUS_COLOR[status] || STATUS_COLOR.pendiente_agendamiento;
  // v2.8.7.3 · Propuesta A2 no aplicada: borde punteado + opacity + no draggable
  const isProposal = status === "propuesta_a2";

  // Duración estimada: 1h por defecto (2 slots)
  const durationSlots = 2;
  const height = durationSlots * SLOT_HEIGHT - 4;

  const t = order.scheduled_at ? new Date(order.scheduled_at) : null;
  const time = t
    ? `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`
    : "";

  return (
    <div
      draggable={canDrag && !isProposal}
      onDragStart={(e) => {
        if (isProposal) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", order.id);
        onDragStart(order);
      }}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className={`absolute left-1 right-1 top-0.5 rounded border-l-4 px-1.5 py-1 shadow-sm hover:shadow-md cursor-pointer overflow-hidden z-10 ${
        saving ? "opacity-50" : ""
      } ${isProposal ? "opacity-70" : ""}`}
      style={{
        backgroundColor: c.bg,
        borderLeftColor: c.border,
        color: c.text,
        height,
        ...(isProposal ? {
          borderStyle: "dashed",
          borderWidth: "1.5px",
          borderColor: c.border,
          borderLeftWidth: "4px",
          backgroundImage: "repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(156,163,175,0.15) 4px,rgba(156,163,175,0.15) 8px)",
        } : {}),
      }}
      title={
        isProposal
          ? `PROPUESTA A2 · No aplicada\n${order.code} · ${order.client_name || ""}\nTéc: ${order._proposal?.tech_name || ""}\nSlot: ${order._proposal?.slot_start?.slice(11,16) || ""} → ${order._proposal?.slot_end?.slice(11,16) || ""}\nTraslado ${order._proposal?.travel_in_min||0}+${order._proposal?.travel_out_min||0}min · Ejec ${order._proposal?.exec_min||0}min`
          : `${order.code} · ${order.client_name || ""}\n${order.address || ""}\n${STATUS_COLOR[status]?.label} · ${order.priority || ""}`
      }
      data-testid={isProposal ? `a2-proposal-block-${order._proposal?.odoo_task_id}` : `order-block-${order.code || order.id.slice(0, 8)}`}
    >
      <div className="flex items-center justify-between gap-1 text-[9px] font-mono">
        <span className="font-bold truncate">{time}</span>
        <span
          className="px-1 rounded text-white text-[8px] font-bold"
          style={{ backgroundColor: c.border }}
        >
          {c.label}
        </span>
        {order.priority && (
          <span className="text-red-700 font-bold">{order.priority}</span>
        )}
      </div>
      <div className="text-[10px] font-bold truncate leading-tight mt-0.5">
        {order.code || order.id.slice(0, 8)}
      </div>
      <div className="text-[9px] truncate opacity-80">
        {order.client_name || "—"}
      </div>
    </div>
  );
}

/* ─── Sidebar de OTs sin agendar ─────────────────────────── */
function UnscheduledSidebar({
  orders,
  onDragStart,
  onDragEnd,
  onDrop,
  onOpenOrder,
  savingId,
}) {
  const [hover, setHover] = useState(false);
  const handleDragOver = (e) => {
    e.preventDefault();
    setHover(true);
  };
  const handleDragLeave = () => setHover(false);
  const handleDropEvt = (e) => {
    e.preventDefault();
    setHover(false);
    onDrop();
  };

  return (
    <div
      className={`w-72 bg-white border rounded-lg overflow-hidden flex flex-col ${
        hover ? "border-amber-400 ring-2 ring-amber-200" : "border-gray-200"
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDropEvt}
      data-testid="unscheduled-sidebar"
    >
      <div className="px-3 py-2 bg-amber-500 text-white flex items-center gap-2">
        <Inbox size={14} />
        <div className="flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wider">
            Sin agendar
          </div>
          <div className="text-[9px] font-mono opacity-90">
            {orders.length} OT · arrastra aquí para desagendar
          </div>
        </div>
      </div>
      <div
        className="flex-1 overflow-y-auto p-2 space-y-1.5"
        style={{ maxHeight: "calc(100vh - 260px)" }}
      >
        {orders.length === 0 ? (
          <div className="text-[11px] text-gray-400 italic text-center py-8">
            Sin OTs por agendar
          </div>
        ) : (
          orders.map((o) => (
            <UnscheduledCard
              key={o.id}
              order={o}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onOpen={() => onOpenOrder(o.id)}
              saving={savingId === o.id}
            />
          ))
        )}
      </div>
    </div>
  );
}

function UnscheduledCard({ order, onDragStart, onDragEnd, onOpen, saving }) {
  const c = STATUS_COLOR[order.status] || STATUS_COLOR.pendiente_agendamiento;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", order.id);
        onDragStart(order);
      }}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className={`rounded border-l-4 bg-white border border-gray-200 p-2 cursor-pointer hover:shadow-md ${
        saving ? "opacity-50" : ""
      }`}
      style={{ borderLeftColor: c.border }}
      data-testid={`unscheduled-${order.code || order.id.slice(0, 8)}`}
    >
      <div className="flex items-center justify-between gap-1 text-[9px] font-mono mb-0.5">
        <span
          className="px-1 rounded text-white text-[8px] font-bold"
          style={{ backgroundColor: c.border }}
        >
          {c.label}
        </span>
        {order.priority && (
          <span className="text-red-700 font-bold">{order.priority}</span>
        )}
      </div>
      <div className="text-[11px] font-bold text-gray-900 truncate">
        {order.code || order.id.slice(0, 8)}
      </div>
      <div className="text-[10px] text-gray-600 truncate">
        {order.client_name || "—"}
      </div>
      {order.address && (
        <div className="text-[9px] text-gray-500 truncate flex items-center gap-1 mt-0.5">
          <MapPin size={9} /> {order.address}
        </div>
      )}
    </div>
  );
}

/* ─── Vista Semana (Google Calendar-style) ─────────────────── */
function WeekView({ startDate, orders, onOpenOrder }) {
  if (!startDate) return null;
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate); d.setDate(d.getDate() + i); return d;
  });
  const dayISO = (d) => d.toISOString().slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);

  const ordersByDay = {};
  for (const d of days) ordersByDay[dayISO(d)] = [];
  for (const o of orders) {
    const iso = (o.scheduled_at || "").slice(0, 10);
    if (ordersByDay[iso]) ordersByDay[iso].push(o);
  }

  const hours = Array.from({ length: 12 }, (_, i) => 7 + i); // 07:00 → 18:00

  return (
    <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 260px)" }}>
      <div className="grid" style={{ gridTemplateColumns: `60px repeat(7, 1fr)` }}>
        {/* Header sticky */}
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 h-14"></div>
        {days.map((d) => {
          const iso = dayISO(d);
          const isToday = iso === today;
          return (
            <div
              key={iso}
              className={`sticky top-0 z-20 bg-white border-b border-gray-200 p-2 text-center ${isToday ? "text-blue-600" : "text-gray-700"}`}
            >
              <div className="text-[9px] uppercase font-mono tracking-widest opacity-70">
                {d.toLocaleDateString("es-EC", {timeZone: "America/Guayaquil",  timeZone: "America/Guayaquil", weekday: "short" })}
              </div>
              <div className={`text-2xl font-bold ${isToday ? "bg-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center mx-auto" : ""}`}>
                {d.getDate()}
              </div>
            </div>
          );
        })}

        {/* Hours grid */}
        {hours.map((h) => (
          <div key={h} className="contents">
            <div className="text-[10px] font-mono text-gray-400 text-right pr-2 -mt-1.5 border-t border-gray-100" style={{ height: 48 }}>
              {String(h).padStart(2, "0")}:00
            </div>
            {days.map((d) => {
              const iso = dayISO(d);
              const cellOrders = ordersByDay[iso].filter((o) => {
                const oh = new Date(o.scheduled_at).getHours();
                return oh === h;
              });
              return (
                <div key={`${iso}-${h}`} className="border-t border-l border-gray-100 relative" style={{ height: 48 }}>
                  {cellOrders.map((o, idx) => {
                    const c = STATUS_COLOR[o.status] || STATUS_COLOR.pendiente_agendamiento;
                    const t = new Date(o.scheduled_at);
                    const isProposal = o.status === "propuesta_a2";
                    return (
                      <div
                        key={o.id}
                        onClick={() => onOpenOrder(o.id)}
                        className={`absolute left-1 right-1 rounded px-1.5 py-0.5 shadow-sm hover:shadow-md cursor-pointer z-10 border-l-4 ${isProposal ? "opacity-70" : ""}`}
                        style={{
                          top: `${idx * 26 + 2}px`,
                          height: "24px",
                          backgroundColor: c.bg,
                          borderLeftColor: c.border,
                          color: c.text,
                          ...(isProposal ? {
                            borderStyle: "dashed",
                            borderWidth: "1.5px",
                            borderColor: c.border,
                            borderLeftWidth: "4px",
                            backgroundImage: "repeating-linear-gradient(45deg,transparent,transparent 3px,rgba(156,163,175,0.18) 3px,rgba(156,163,175,0.18) 6px)",
                          } : {}),
                        }}
                        title={isProposal
                          ? `PROPUESTA A2 · No aplicada\n${o.code} · ${o.client_name || ""}\nTéc: ${o._proposal?.tech_name || ""}`
                          : `${o.code} · ${o.client_name || ""}`}
                        data-testid={isProposal ? `a2-proposal-block-${o._proposal?.odoo_task_id}` : `week-order-${o.code || o.id.slice(0, 8)}`}
                      >
                        <div className="text-[9px] font-mono font-bold truncate">
                          {String(t.getHours()).padStart(2, "0")}:{String(t.getMinutes()).padStart(2, "0")} · {o.code}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Vista Mes (Google Calendar-style) ────────────────────── */
function MonthView({ startDate, anchor, orders, onOpenOrder, onPickDay }) {
  if (!startDate) return null;
  const totalDays = 42; // 6 semanas × 7 días
  const days = Array.from({ length: totalDays }, (_, i) => {
    const d = new Date(startDate); d.setDate(d.getDate() + i); return d;
  });
  const dayISO = (d) => d.toISOString().slice(0, 10);
  const todayISO = new Date().toISOString().slice(0, 10);
  const currentMonth = anchor.getMonth();

  const ordersByDay = {};
  for (const o of orders) {
    const iso = (o.scheduled_at || "").slice(0, 10);
    if (!ordersByDay[iso]) ordersByDay[iso] = [];
    ordersByDay[iso].push(o);
  }

  return (
    <div>
      {/* Header con días de la semana */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((d) => (
          <div key={d} className="text-[10px] uppercase font-mono tracking-widest text-gray-500 text-center py-2">
            {d}
          </div>
        ))}
      </div>
      {/* Grid mensual */}
      <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(88px, 1fr)" }}>
        {days.map((d) => {
          const iso = dayISO(d);
          const isToday = iso === todayISO;
          const isOtherMonth = d.getMonth() !== currentMonth;
          const dayOrders = ordersByDay[iso] || [];
          return (
            <div
              key={iso}
              onClick={() => onPickDay(d)}
              className={`border-t border-l border-gray-100 p-1.5 cursor-pointer hover:bg-blue-50 ${isOtherMonth ? "bg-gray-50 text-gray-400" : "bg-white"}`}
              data-testid={`month-day-${iso}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-xs font-bold ${
                    isToday
                      ? "bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center"
                      : isOtherMonth ? "text-gray-400" : "text-gray-700"
                  }`}
                >
                  {d.getDate()}
                </span>
                {dayOrders.length > 0 && (
                  <span className="text-[9px] font-mono text-gray-500">{dayOrders.length} OT</span>
                )}
              </div>
              <div className="space-y-0.5">
                {dayOrders.slice(0, 3).map((o) => {
                  const c = STATUS_COLOR[o.status] || STATUS_COLOR.pendiente_agendamiento;
                  const isProposal = o.status === "propuesta_a2";
                  return (
                    <div
                      key={o.id}
                      onClick={(e) => { e.stopPropagation(); onOpenOrder(o.id); }}
                      className={`text-[9px] font-mono truncate rounded px-1 py-0.5 border-l-2 hover:brightness-95 ${isProposal ? "opacity-70" : ""}`}
                      style={{
                        backgroundColor: c.bg,
                        borderLeftColor: c.border,
                        color: c.text,
                        ...(isProposal ? { borderStyle: "dashed", borderWidth: "1px", borderLeftWidth: "2px" } : {}),
                      }}
                      title={isProposal ? `PROPUESTA A2 · ${o.code} · ${o.client_name || ""}` : `${o.code} · ${o.client_name || ""}`}
                    >
                      {o.code}
                    </div>
                  );
                })}
                {dayOrders.length > 3 && (
                  <div className="text-[9px] text-gray-500 pl-1">+ {dayOrders.length - 3} más</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

